/**
 * TanStack Query hooks for the plan page's Rate Card panel (RP-6.6, RP-12.6, RP-12.8).
 * Contract: src/schemas/plan-rate-card.ts; handlers: src/api/plan-rate-card.ts.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useInvalidatePlans } from "@/hooks/use-resource-plans";
import { jsonFetch } from "@/lib/json-fetch";
import { resourcePlanKeys } from "@/lib/query";
import type {
  ChangePlanTierInput,
  PlanRateCardView,
  PlanRateHistoryView,
  PlanTierChangeResult,
  PlanTierPreviewView,
} from "@/schemas/plan-rate-card";

export type {
  PlanRateCardCategoryView,
  PlanRateCardView,
  PlanRateHistoryView,
  PlanTierPreviewView,
} from "@/schemas/plan-rate-card";

export function usePlanRateCard(planId: string) {
  return useQuery({
    queryKey: resourcePlanKeys.rateCard(planId),
    queryFn: () => jsonFetch<PlanRateCardView>(`/api/plans/${planId}/rate-card`),
  });
}

/** One Labor Category's rates over time — fetched only when its row is expanded. */
export function usePlanRateHistory(planId: string, laborCategoryId: string, enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.rateHistory(planId, laborCategoryId),
    queryFn: () =>
      jsonFetch<PlanRateHistoryView>(
        `/api/plans/${planId}/rate-card/history?laborCategoryId=${encodeURIComponent(laborCategoryId)}`,
      ),
    enabled,
  });
}

/** What moving the Account to `tierId` would do — fetched while the confirm dialog is open. */
export function usePlanTierPreview(
  planId: string,
  tierId: string | null,
  alsoSetSolutionTier: boolean,
) {
  return useQuery({
    queryKey: resourcePlanKeys.tierPreview(planId, tierId ?? "", alsoSetSolutionTier),
    queryFn: () => {
      const qs = new URLSearchParams({
        tierId: tierId ?? "",
        alsoSetSolutionTier: String(alsoSetSolutionTier),
      });
      return jsonFetch<PlanTierPreviewView>(`/api/plans/${planId}/rate-card/tier-preview?${qs}`);
    },
    enabled: tierId !== null,
  });
}

export function useChangePlanTier(planId: string) {
  // Plans, the Solutions Dashboard and the Account page all show the tier or the rates.
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: ChangePlanTierInput) =>
      jsonFetch<PlanTierChangeResult>(`/api/plans/${planId}/rate-card/tier`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: (result) => {
      invalidate();
      toast.success(
        result.repriced
          ? `Account moved to the ${result.accountTierName} Engagement Standard Tier. ${result.repricedLineCount} ${result.repricedLineCount === 1 ? "Plan Line" : "Plan Lines"} re-priced on ${result.rateBasisLabel}.`
          : `Account moved to the ${result.accountTierName} Engagement Standard Tier. This plan's rates did not change.`,
      );
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Could not change the Engagement Standard Tier.",
      );
    },
  });
}
